<script lang="ts">
  import { Link, router } from '@inertiajs/svelte'
  import ArrowRight from '@lucide/svelte/icons/arrow-right'
  import Sparkles from '@lucide/svelte/icons/sparkles'
  import X from '@lucide/svelte/icons/x'

  let {
    latest,
    missedCount,
    acknowledgeThrough,
    acknowledgePath,
    changelogPath,
    compact = false,
  }: {
    latest: {
      version: string
      title: string
      description: string
    }
    missedCount: number
    acknowledgeThrough: number
    acknowledgePath: string
    changelogPath: string
    compact?: boolean
  } = $props()

  function dismiss() {
    router.patch(
      acknowledgePath,
      { through: acknowledgeThrough },
      { preserveScroll: true }
    )
  }
</script>

{#if compact}
  <section
    aria-label="What's new"
    class="relative rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 shadow-sm dark:border-primary-800 dark:bg-gray-800 sm:px-4"
  >
    <div class="flex min-w-0 items-center gap-2 pr-10 text-sm">
      <div
        class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-white"
      >
        <Sparkles size={15} aria-hidden="true" />
      </div>
      <p class="min-w-0 flex-1 truncate text-neutral-700 dark:text-neutral-200">
        <span class="font-semibold text-primary-700 dark:text-primary-300"
          >What's new:</span
        >
        <span class="font-medium"> {latest.title}</span>
        {#if missedCount > 1}
          <span class="hidden text-neutral-500 dark:text-neutral-400 sm:inline">
            · {missedCount} updates</span
          >
        {/if}
      </p>
      <Link
        href={changelogPath}
        class="shrink-0 font-semibold text-primary-700 hover:underline dark:text-primary-300"
        >More</Link
      >
    </div>
    <button
      type="button"
      aria-label="Dismiss what's new"
      class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-neutral-400 dark:hover:bg-gray-700 dark:hover:text-white"
      onclick={dismiss}
    >
      <X size={16} aria-hidden="true" />
    </button>
  </section>
{:else}
  <section
    aria-labelledby="whats-new-heading"
    class="relative mb-7 rounded-2xl border border-primary-200 bg-primary-50 p-5 shadow-sm dark:border-primary-800 dark:bg-gray-800 sm:p-6"
  >
    <button
      type="button"
      aria-label="Dismiss what's new"
      class="absolute right-3 top-3 z-10 rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-neutral-400 dark:hover:bg-gray-700 dark:hover:text-white"
      onclick={dismiss}
    >
      <X size={18} aria-hidden="true" />
    </button>

    <div class="relative flex items-start gap-4 pr-10">
      <div
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-white shadow-sm"
      >
        <Sparkles size={20} aria-hidden="true" />
      </div>

      <div class="min-w-0 flex-1">
        <p
          class="text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-300"
        >
          What's new · {latest.version}
        </p>
        <h2
          id="whats-new-heading"
          class="mt-1 text-xl font-bold tracking-tight text-neutral-900 dark:text-white"
        >
          {latest.title}
        </h2>
        <p
          class="mt-1.5 line-clamp-3 text-sm leading-6 text-neutral-600 dark:text-neutral-300"
        >
          {latest.description}
        </p>

        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p class="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {#if missedCount === 1}
              A new update is ready
            {:else}
              {missedCount} updates since you last checked in
            {/if}
          </p>
          <Link
            href={changelogPath}
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline dark:text-primary-300"
          >
            See what's new <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  </section>
{/if}
